import { ShowSection } from 'framework/ui/layout';
import { FormShowTemplate } from 'framework/form';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  BudgetsFinanceType,
  BudgetsType,
  useProjectFinanceService,
  useProjectSecurityContext,
  useProjectDataContext,
} from 'domain/shared/newProject';
import { keyBy, keys, union } from 'lodash';
import { EditableListFormShowTemplate } from 'framework/list';
import BudgetRenderer from './BudgetRenderer';

const BudgetContext = () => {
  const { budgetsSearch, budgetsFinanceSearch } = useProjectFinanceService();
  const { data } = useProjectDataContext();
  const { isAccessGranted } = useProjectSecurityContext();

  const mergeBudgets = (budgetMain: BudgetsType, budgetFinance?: BudgetsFinanceType) => {
    const budgetStructures = keyBy(budgetMain?.structure || [], 'id');
    const financeStructures = keyBy(budgetFinance?.structure || [], 'id');

    const allStructuresIds = union(keys(budgetStructures), keys(financeStructures));
    const structures = allStructuresIds.map((structureId: string) => ({
      id: structureId,
      source: budgetStructures[structureId]?.source || financeStructures[structureId]?.source,
      budgetStartDate: budgetMain?.startDate,
      budgetSoftStartDate: budgetMain?.softStartDate,
      budgetEndDate: budgetMain?.endDate,
      budgetExtendedEndDate: budgetMain?.extendedEndDate,
      budget: budgetStructures[structureId] || null,
      finance: financeStructures[structureId] || null,
    }));

    return {
      ...budgetMain,
      structure: structures,
      finance: budgetFinance,
    };
  };

  const getDataFunc = async () => {
    if (!data.id) {
      throw new Error('Project ID is required');
    }
    const budgets = await budgetsSearch({
      pageNumber: 0,
      pageSize: 10,
      filter: {
        id: {
          eq: data?.budgetData?.defaultBudgetId,
        },
      },
    }).then(budgetsResponse => budgetsResponse?.result);

    const finances = await budgetsFinanceSearch({
      page: 0,
      size: budgets.length,
      sort: {
        createdAt: 'ASC',
      },
      filter: {
        id: {
          in: budgets.map((item: BudgetsType) => `${item.id}`),
        },
      },
    }).then(financesResponse => financesResponse?.result);

    return Promise.resolve({
      result: budgets.map((item: BudgetsType) => {
        const finance = finances.find((financeItem: BudgetsFinanceType) => item.id === financeItem.id);
        return mergeBudgets(item, finance);
      }),
    });
  };

  return (
    <ShowSection
      sx={{ '&.section-details__wrapper': { p: '0px !important' } }}
      isVisible={isAccessGranted(ACCESS_INFO_SECTIONS.BUDGET, ACCESS_INFO_ACTIONS.VIEW)}
    >
      <FormShowTemplate
        isInitialsLoadingPosition
        submitFormFunc={values => Promise.resolve(() => {})}
        afterSubmit={{
          message: 'You are awesome! Budget information has been successfully updated.',
        }}
        getDataFunc={getDataFunc}
      >
        <EditableListFormShowTemplate listSource="result">
          <BudgetRenderer />
        </EditableListFormShowTemplate>
      </FormShowTemplate>
    </ShowSection>
  );
};

export default BudgetContext;
